import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Input primitive — mirrors `.ez-input` from design/app-tokens.css.
 * 40px height, line-strong border, focus = volt-500 border + 3px volt-glow ring.
 * Pass `error` to swap to the danger palette (red border + red focus ring).
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, error = false, type = 'text', ...props }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        data-error={error || undefined}
        className={cn(
          'w-full h-10 px-3 text-sm bg-surface text-text rounded-md border outline-none transition-[border-color,box-shadow] duration-100',
          'placeholder:text-text-4',
          // Default state
          !error && 'border-line-strong hover:border-text-3',
          !error && 'focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow',
          // Error state
          error && 'border-danger',
          error && 'focus:ring-[3px] focus:ring-[rgba(220,38,38,0.18)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';
